import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { BuildingOffice, Check, Phone, Envelope, Star } from "@phosphor-icons/react";
import ZoneSection from "@/components/ZoneSection";
import ProQuoteForm from "@/components/ProQuoteForm";
import Seo from "@/components/Seo";

const proFormulas = [
  {
    id: "pro-digital",
    name: "PRO DIGITAL",
    price: 149,
    tagline: "L'essentiel digital",
    features: [
      "Photobooth",
      "Photos numériques illimitées",
      "Flash LED",
      "Personnalisation du cadre photo",
      "Intégration du logo de l'entreprise",
      "Récupération des photos par QR Code",
      "Installation et récupération incluses",
      "Jusqu'à 6 heures de location",
    ],
    recommended: false,
  },
  {
    id: "pro-print",
    name: "PRO PRINT",
    price: 249,
    tagline: "Digital + Impressions",
    features: [
      "Tout le contenu de la formule PRO DIGITAL",
      "200 impressions incluses",
      "Impression photo instantanée",
      "Personnalisation du visuel d'impression",
      "Logo et identité visuelle de l'entreprise",
      "Installation et récupération incluses",
      "Jusqu'à 6 heures de location",
    ],
    recommended: true,
  },
];

const eventTypes = [
  "Séminaires",
  "Inaugurations",
  "Salons professionnels",
  "Soirées d'entreprise",
  "Team building",
  "Lancements de produit",
];

function ProCard({ formula }) {
  const isRec = formula.recommended;
  return (
    <div
      className={`relative flex flex-col h-full rounded-2xl transition-all ${
        isRec
          ? "bg-[#0A0A0A] border-2 border-[#C9A962] shadow-[0_0_40px_rgba(201,169,98,0.3)] md:scale-[1.02]"
          : "bg-[#0A0A0A] border border-[#C9A962]/25 hover:border-[#C9A962]/50"
      }`}
      data-testid={`pro-card-${formula.id}`}
    >
      {isRec && (
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
          <div className="flex items-center gap-1.5 bg-gradient-to-r from-[#D4BA7A] to-[#C9A962] text-black text-sm font-bold px-5 py-2 rounded-full shadow-lg">
            <Star size={16} weight="fill" />
            Recommandée
          </div>
        </div>
      )}

      <div className="p-8 md:p-10 flex flex-col flex-grow">
        {/* Header */}
        <div className="text-center mb-8 pt-3">
          <h3 className="text-2xl font-bold text-white mb-1">{formula.name}</h3>
          <p className="text-[11px] uppercase tracking-[0.25em] text-[#C9A962]/80 mb-4">
            {formula.tagline}
          </p>
          <div className="flex items-baseline justify-center gap-1">
            <span className="text-5xl md:text-6xl font-extrabold gradient-text">{formula.price}</span>
            <span className="text-2xl gradient-text">€</span>
            <span className="ml-2 text-sm text-[#8A7A5A] font-medium tracking-wider">HT</span>
          </div>
        </div>

        {/* Features */}
        <ul className="space-y-3 mb-8 flex-grow">
          {formula.features.map((feature) => (
            <li key={feature} className="flex items-start gap-3">
              <div className="mt-1 w-5 h-5 rounded-full bg-gradient-to-br from-[#C9A962] to-[#A88B4A] flex items-center justify-center flex-shrink-0">
                <Check size={12} weight="bold" className="text-black" />
              </div>
              <span className="text-[#B8A88A] text-sm leading-snug break-words">{feature}</span>
            </li>
          ))}
        </ul>

        {/* CTA */}
        <div className="mt-auto">
          <Link to="/contact" data-testid={`pro-contact-cta-${formula.id}`}>
            <button
              className={`w-full py-4 rounded-full font-bold transition-all ${
                isRec
                  ? "bg-gradient-to-r from-[#D4BA7A] to-[#C9A962] text-black text-lg shadow-[0_0_20px_rgba(201,169,98,0.4)] hover:shadow-[0_0_30px_rgba(201,169,98,0.6)]"
                  : "border-2 border-[#C9A962] text-[#C9A962] hover:bg-[#C9A962]/10"
              }`}
            >
              Demander un devis
            </button>
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function TarifsProfessionnels() {
  return (
    <div className="pt-24 md:pt-32 lg:pt-36" data-testid="tarifs-pro-page">
      <Seo
        title="Photobooth Entreprise & Événement Pro Nord-Isère | Pixora"
        description="Location de photobooth pour vos événements professionnels à Bourgoin-Jallieu et en Nord-Isère. Formules Pro Digital 149€ HT et Pro Print 249€ HT, devis instantané."
        path="/tarifs-professionnels"
      />
      {/* Hero */}
      <section className="py-14 md:py-20 bokeh-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-14"
          >
            <div className="flex items-center justify-center gap-2 mb-4">
              <BuildingOffice size={20} className="text-[#C9A962]" weight="fill" />
              <span className="text-sm uppercase tracking-[0.2em] text-[#B8A88A]">
                Pixora Pro
              </span>
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-white mb-4">
              Formules <span className="gradient-text">Professionnelles</span>
            </h1>
            <p className="text-base md:text-lg text-[#B8A88A] max-w-2xl mx-auto">
              Deux formules simples et adaptées à vos événements corporate.
            </p>
          </motion.div>

          {/* Two pricing cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10 max-w-4xl mx-auto mb-16 pt-4">
            {proFormulas.map((formula, index) => (
              <motion.div
                key={formula.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <ProCard formula={formula} />
              </motion.div>
            ))}
          </div>

          {/* HT notice */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center text-[#8A7A5A] text-sm mb-16"
          >
            <p>Tarifs affichés hors taxes (HT) — TVA applicable selon la réglementation en vigueur.</p>
          </motion.div>

          {/* Event types */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto text-center mb-20"
          >
            <h2 className="text-xl md:text-2xl font-bold text-white mb-6">
              Adapté à tous vos <span className="gradient-text">événements</span>
            </h2>
            <div className="flex flex-wrap gap-2 justify-center">
              {eventTypes.map((type) => (
                <span
                  key={type}
                  className="px-4 py-2 rounded-full bg-[#0A0A0A] border border-[#C9A962]/30 text-[#B8A88A] text-sm"
                >
                  {type}
                </span>
              ))}
            </div>
          </motion.div>

          {/* Devis express form */}
          <ProQuoteForm />

          {/* Contact info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-14"
          >
            <p className="text-[#B8A88A] mb-4">Besoin d'informations rapides ?</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              <a
                href="tel:0745709881"
                className="flex items-center gap-2 text-white hover:text-[#C9A962] transition-colors"
                data-testid="pro-phone-link"
              >
                <Phone size={20} className="text-[#C9A962]" />
                07 45 70 98 81
              </a>
              <a
                href="mailto:pixora.photobooth38@gmail.com"
                className="flex items-center gap-2 text-white hover:text-[#C9A962] transition-colors"
                data-testid="pro-email-link"
              >
                <Envelope size={20} className="text-[#C9A962]" />
                pixora.photobooth38@gmail.com
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <ZoneSection />
    </div>
  );
}
